import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mainSiteUrl } from '@/mocks/categories';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d9cbiamdn0rfb35c5k6g';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypotVal = (formData.get('company_alt') as string || '').trim();
    if (honeypotVal) {
      setSubmitStatus('success');
      setEmail('');
      return;
    }
    formData.delete('company_alt');

    try {
      const res = await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
      if (res.ok && (parsed as { code?: string }).code === 'OK') {
        setSubmitStatus('success');
        setEmail('');
        setFormError('');
      } else {
        const msg = (parsed as { meta?: { message?: string } })?.meta?.message || '';
        if (msg.includes('spam')) {
          setSubmitStatus('success');
          setEmail('');
        } else {
          setSubmitStatus('error');
          setFormError(msg || 'Ein Fehler ist aufgetreten.');
        }
      }
    } catch {
      setSubmitStatus('error');
      setFormError('Netzwerkfehler. Bitte versuche es später erneut.');
    }
  };

  return (
    <footer className="bg-accent-900 text-background-50">
      <div className="mx-auto max-w-[1440px] px-4 md:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-1">
            <a href="/" className="flex items-center gap-2 no-underline mb-4 cursor-pointer">
              <div className="w-9 h-9 rounded-full bg-accent-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className="font-heading font-bold text-xl text-background-50 whitespace-nowrap">
                OOPBuy
              </span>
            </a>
            <p className="text-sm text-background-50/60 leading-relaxed">
              {t('footer.brand.desc')}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-background-50 mb-4 uppercase tracking-wider">
              {t('footer.navigation')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="/" className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav.home')}
                </a>
              </li>
              <li>
                <a href="/artikel/oopbuy-spreadsheet-guide" className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  Einkaufsratgeber
                </a>
              </li>
              <li>
                <a href="/landing/beste-schuhe" className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  Beste Schuhe
                </a>
              </li>
              <li>
                <a href="/landing/beste-hoodies" className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  Beste Hoodies
                </a>
              </li>
              <li>
                <a href="/landing/beste-verkaeufer" className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  Beste Verkäufer
                </a>
              </li>
              <li>
                <a href="#faq" className="text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  {t('footer.nav.faq')}
                </a>
              </li>
            </ul>
          </div>

          {/* Social */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-background-50 mb-4 uppercase tracking-wider">
              {t('footer.social')}
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a href="#" className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                  Instagram
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  <i className="ri-youtube-line"></i>
                  YouTube
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-2 text-sm text-background-50/60 hover:text-background-50 transition-colors cursor-pointer">
                  <i className="ri-facebook-line"></i>
                  Facebook
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-heading font-semibold text-sm text-background-50 mb-4 uppercase tracking-wider">
              {t('footer.newsletter')}
            </h4>
            <p className="text-sm text-background-50/60 mb-3">
              {t('footer.newsletter.desc')}
            </p>
            <form onSubmit={handleNewsletterSubmit} data-readdy-form="true">
              <div className="honeypot-wrapper">
                <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('footer.newsletter.placeholder')}
                  required
                  className="flex-1 px-4 py-2.5 rounded-full bg-background-50/10 border border-background-50/20 text-sm text-background-50 placeholder:text-background-50/40 focus:outline-none focus:border-accent-500 transition-colors"
                />
                <button
                  type="submit"
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 hover:bg-accent-400 transition-colors cursor-pointer flex-shrink-0"
                >
                  <i className="ri-arrow-right-line"></i>
                </button>
              </div>
              {submitStatus === 'success' && (
                <p className="text-xs text-green-400 mt-2">Danke! Du hast dich erfolgreich angemeldet.</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-xs text-red-400 mt-2">{formError || 'Ein Fehler ist aufgetreten.'}</p>
              )}
            </form>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-12 pt-8 border-t border-background-50/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-50/40">{t('footer.copyright')}</p>
          <div className="flex items-center gap-4">
            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs text-background-50/40 hover:text-background-50/70 transition-colors cursor-pointer">
              kaspreadsheet.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}